import { useFeatureFlags } from "@/contexts/FeatureFlagsContext";
import { useDebug, NetworkRequest } from "@/contexts/DebugContext";
import { X, ChevronDown, ChevronRight, Trash2, Eye, EyeOff, RefreshCw } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { clearCacheForEndpoint, getQueryKeyForEndpoint, extractEndpointName, clearAllCaches, listCaches, markQueryKeyCleared, type CacheInfo } from "@/lib/cacheRegistry";
import { useQueryClient } from "@tanstack/react-query";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth } from "@/contexts/AuthContext";
import { getAllColumnPreferences, clearColumnPreferences } from "@/hooks/useColumnPreferences";

export interface SimulatedEndpoint {
  id: string;
  label: string;
  description?: string;
  responses: { status: number; label: string }[];
}

export type LayoutTemplate = 
  | "faceted-browse" // Products, Digital Assets - sidebar facets, own header
  | "timeline" // MessageBoard - timeline layout
  | "data-table" // VINs, Orders, etc. - DataTable with StandardPagination
  | "content-standard" // Basic ContentLayout wrapper
  | "content-form" // Form-based pages
  | "dashboard" // Dashboard with cards/widgets
  | "detail" // Detail pages (single item view)
  | "auth" // Authentication pages
  | "print" // Print-optimized pages
  | "full-width"; // Full-width without ContentLayout

export interface StyleHighlight {
  id: string;
  label: string;
  selector?: string; // CSS selector to highlight
  description?: string;
}

interface DebugPanelProps {
  layoutTemplate: LayoutTemplate;
  customStyles?: StyleHighlight[];
  pageName?: string;
  simulatedEndpoints?: SimulatedEndpoint[];
}

const layoutDescriptions: Record<LayoutTemplate, string> = {
  "faceted-browse": "Sidebar facets, own 'Showing X of Y' header with Sort/Show dropdowns, compact pagination at bottom only",
  "timeline": "Timeline layout with StandardPagination top + compact bottom",
  "data-table": "DataTable component with StandardPagination top + compact bottom, optional column toggle",
  "content-standard": "Basic page wrapped in ContentLayout with consistent padding/width",
  "content-form": "Form-based page with ContentLayout, typically single-column",
  "dashboard": "Dashboard layout with cards, widgets, and overview sections",
  "detail": "Single item detail view, typically with back navigation",
  "auth": "Authentication pages (login, register, etc.)",
  "print": "Print-optimized layout without navigation elements",
  "full-width": "Full-width layout without max-width constraints"
};

function formatUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    const path = urlObj.pathname.split('/').pop() || urlObj.pathname;
    return path;
  } catch {
    return url.slice(0, 30);
  }
}

function formatDuration(ms?: number): string {
  if (!ms) return '-';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

function RequestItem({ request }: { request: NetworkRequest }) {
  const [expanded, setExpanded] = useState(false);
  const [cacheCleared, setCacheCleared] = useState(false);
  const queryClient = useQueryClient();
  
  const statusColor = request.error 
    ? 'text-red-600' 
    : request.status && request.status >= 400 
      ? 'text-red-600' 
      : 'text-green-600';

  const endpointName = extractEndpointName(request.url);

  const handleClearCache = (e: React.MouseEvent) => {
    e.stopPropagation();
    clearCacheForEndpoint(endpointName);
    const queryKey = getQueryKeyForEndpoint(endpointName);
    if (queryKey) {
      queryClient.invalidateQueries({ queryKey });
      markQueryKeyCleared(`${queryKey.join('/')} (React Query)`);
    }
    setCacheCleared(true);
    setTimeout(() => setCacheCleared(false), 1500);
  };

  return (
    <div className="border-b border-amber-200 last:border-b-0">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 py-1.5 px-2 hover:bg-amber-200/50 transition-colors text-left"
      >
        {expanded ? (
          <ChevronDown className="h-3 w-3 flex-shrink-0" />
        ) : (
          <ChevronRight className="h-3 w-3 flex-shrink-0" />
        )}
        <span className={cn("font-semibold text-[10px] w-10", statusColor)}>
          {request.method}
        </span>
        <span className="flex-1 truncate text-[10px]" title={request.url}>
          {formatUrl(request.url)}
        </span>
        <span className={cn("text-[10px]", statusColor)}>
          {request.status || (request.error ? 'ERR' : '...')}
        </span>
        <span className="text-[10px] text-amber-600 w-12 text-right">
          {formatDuration(request.duration)}
        </span>
        <span
          onClick={handleClearCache}
          className={cn(
            "p-0.5 rounded transition-colors flex-shrink-0",
            cacheCleared
              ? "text-green-600"
              : "text-amber-500 hover:text-amber-800 hover:bg-amber-200"
          )}
          title={cacheCleared ? "Cache cleared!" : `Clear cache for ${endpointName}`}
        >
          <RefreshCw className={cn("h-3 w-3", cacheCleared && "animate-spin")} />
        </span>
      </button>
      
      {expanded && (
        <div className="px-2 pb-2 space-y-2 text-[10px] bg-amber-50">
          <div>
            <div className="font-semibold text-amber-700">URL:</div>
            <div className="break-all text-amber-900 bg-amber-100 p-1 rounded">
              {request.url}
            </div>
          </div>
          
          {request.requestBody && (
            <div>
              <div className="font-semibold text-amber-700">Request Body:</div>
              <pre className="bg-amber-100 p-1 rounded overflow-x-auto max-h-24 text-amber-900">
                {JSON.stringify(request.requestBody, null, 2)}
              </pre>
            </div>
          )}
          
          {request.responseBody && (
            <div>
              <div className="font-semibold text-amber-700">Response:</div>
              <pre className="bg-amber-100 p-1 rounded overflow-x-auto max-h-32 text-amber-900">
                {JSON.stringify(request.responseBody, null, 2)}
              </pre>
            </div>
          )}
          
          {request.error && (
            <div>
              <div className="font-semibold text-red-700">Error:</div>
              <div className="text-red-600 bg-red-100 p-1 rounded">{request.error}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function DebugPanel({ layoutTemplate, customStyles = [], pageName, simulatedEndpoints = [] }: DebugPanelProps) {
  const { flags } = useFeatureFlags();
  const { requests, clearRequests, highlightedStyle, setHighlightedStyle, simulatedResponses, setSimulatedResponse } = useDebug();
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('debug-panel-collapsed');
    return saved !== null ? saved === 'true' : true;
  });
  const [activeTab, setActiveTab] = useState<'layout' | 'requests' | 'simulate' | 'columns' | 'caches'>('layout');
  const [showRequests, setShowRequests] = useState(true);
  const [allCacheCleared, setAllCacheCleared] = useState(false);
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const username = user?.username || 'anonymous';
  const [columnPrefsVersion, setColumnPrefsVersion] = useState(0);
  const [cachesVersion, setCachesVersion] = useState(0);

  const isDebugMode = flags.debugMode;

  if (!isDebugMode) return null;

  // Handle style highlighting
  const handleStyleClick = (style: StyleHighlight) => {
    if (highlightedStyle === style.id) {
      setHighlightedStyle(null);
      removeHighlights();
    } else {
      setHighlightedStyle(style.id);
      applyHighlight(style.selector);
    }
  };

  const applyHighlight = (selector?: string) => {
    removeHighlights();
    if (!selector) return;
    
    try {
      const elements = document.querySelectorAll(selector);
      elements.forEach(el => {
        (el as HTMLElement).style.outline = '3px solid #f59e0b';
        (el as HTMLElement).style.outlineOffset = '2px';
        el.classList.add('debug-highlighted');
      });
    } catch (e) {
      console.warn('Invalid selector:', selector);
    }
  };

  const removeHighlights = () => {
    document.querySelectorAll('.debug-highlighted').forEach(el => {
      (el as HTMLElement).style.outline = '';
      (el as HTMLElement).style.outlineOffset = '';
      el.classList.remove('debug-highlighted');
    });
  };

  if (isCollapsed) {
    return (
      <button
        onClick={() => {
          setIsCollapsed(false);
          localStorage.setItem('debug-panel-collapsed', 'false');
        }}
        className="fixed bottom-4 right-4 z-[9999] bg-yellow-500 text-yellow-950 px-3 py-1.5 rounded-full text-xs font-bold shadow-lg hover:bg-yellow-400 transition-colors"
        data-debug-panel
      >
        🔧 Debug
      </button>
    );
  }

  return (
    <div data-debug-panel className="fixed bottom-4 right-4 z-[9999] bg-yellow-100 border-2 border-yellow-500 rounded-lg shadow-xl w-96 max-h-[70vh] flex flex-col text-xs">
      <div className="bg-yellow-500 text-yellow-950 px-3 py-1.5 rounded-t-md flex items-center justify-between flex-shrink-0">
        <span className="font-bold">🔧 Debug Mode</span>
        <button
          onClick={() => {
            setIsCollapsed(true);
            localStorage.setItem('debug-panel-collapsed', 'true');
            removeHighlights();
            setHighlightedStyle(null);
          }}
          className="hover:bg-yellow-600 rounded p-0.5 transition-colors"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      
      {/* Tabs */}
      <div className="flex border-b border-yellow-300 flex-shrink-0">
        <button
          onClick={() => setActiveTab('layout')}
          className={cn(
            "flex-1 px-3 py-1.5 text-[11px] font-medium transition-colors",
            activeTab === 'layout' 
              ? "bg-yellow-200 text-yellow-900" 
              : "text-yellow-700 hover:bg-yellow-50"
          )}
        >
          Layout Info
        </button>
        <button
          onClick={() => setActiveTab('requests')}
          className={cn(
            "flex-1 px-3 py-1.5 text-[11px] font-medium transition-colors relative",
            activeTab === 'requests' 
              ? "bg-yellow-200 text-yellow-900" 
              : "text-yellow-700 hover:bg-yellow-50"
          )}
        >
          API Requests
          {requests.length > 0 && (
            <span className="ml-1 bg-yellow-500 text-yellow-950 px-1.5 py-0.5 rounded-full text-[9px]">
              {requests.length}
            </span>
          )}
        </button>
        {simulatedEndpoints.length > 0 && (
          <button
            onClick={() => setActiveTab('simulate')}
            className={cn(
              "flex-1 px-3 py-1.5 text-[11px] font-medium transition-colors",
              activeTab === 'simulate' 
                ? "bg-yellow-200 text-yellow-900" 
                : "text-yellow-700 hover:bg-yellow-50"
            )}
          >
            Simulate
            {Object.keys(simulatedResponses).length > 0 && (
              <span className="ml-1 bg-orange-500 text-white px-1.5 py-0.5 rounded-full text-[9px]">
                {Object.keys(simulatedResponses).length}
              </span>
            )}
          </button>
        )}
        <button
          onClick={() => setActiveTab('columns')}
          className={cn(
            "flex-1 px-3 py-1.5 text-[11px] font-medium transition-colors",
            activeTab === 'columns' 
              ? "bg-yellow-200 text-yellow-900" 
              : "text-yellow-700 hover:bg-yellow-50"
          )}
        >
          Columns
        </button>
        <button
          onClick={() => setActiveTab('caches')}
          className={cn(
            "flex-1 px-3 py-1.5 text-[11px] font-medium transition-colors",
            activeTab === 'caches'
              ? "bg-yellow-200 text-yellow-900"
              : "text-yellow-700 hover:bg-yellow-50"
          )}
        >
          Caches
        </button>
      </div>

      <div className="overflow-y-auto flex-1">
        {activeTab === 'layout' ? (
          <div className="p-3 space-y-3 text-yellow-950">
            {pageName && (
              <div>
                <div className="font-bold text-yellow-700 mb-0.5">Page:</div>
                <div className="bg-yellow-200/50 px-2 py-1 rounded">{pageName}</div>
              </div>
            )}
            
            <div>
              <div className="font-bold text-yellow-700 mb-0.5">Layout Template:</div>
              <div className="bg-yellow-200/50 px-2 py-1 rounded">
                <span className="font-semibold">{layoutTemplate}</span>
              </div>
              <div className="mt-1 text-[10px] text-yellow-700 leading-tight">
                {layoutDescriptions[layoutTemplate]}
              </div>
            </div>

            <div>
              <div className="font-bold text-yellow-700 mb-1">
                Custom Styles 
                <span className="font-normal text-[10px] ml-1">(click to highlight)</span>
              </div>
              {customStyles.length > 0 ? (
                <div className="space-y-1">
                  {customStyles.map((style) => (
                    <button
                      key={style.id}
                      onClick={() => handleStyleClick(style)}
                      className={cn(
                        "w-full text-left px-2 py-1.5 rounded transition-colors flex items-start gap-2",
                        highlightedStyle === style.id
                          ? "bg-amber-400 text-amber-950"
                          : "bg-yellow-200/50 hover:bg-yellow-200 text-yellow-900"
                      )}
                    >
                      {highlightedStyle === style.id ? (
                        <Eye className="h-3 w-3 mt-0.5 flex-shrink-0" />
                      ) : (
                        <EyeOff className="h-3 w-3 mt-0.5 flex-shrink-0 opacity-50" />
                      )}
                      <div>
                        <div className="font-medium">{style.label}</div>
                        {style.selector && (
                          <div className="text-[9px] opacity-70">{style.selector}</div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="bg-yellow-200/50 px-2 py-1 rounded text-yellow-600 italic">
                  No custom styles (uses template defaults)
                </div>
              )}
            </div>
          </div>
        ) : activeTab === 'requests' ? (
          <div className="text-yellow-950">
            <div className="flex items-center justify-between px-2 py-1.5 border-b border-yellow-300 bg-yellow-50">
              <span className="text-[10px] text-yellow-700">
                {requests.length} request{requests.length !== 1 ? 's' : ''} captured
              </span>
              <div className="flex items-center gap-1">
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => {
                          const queryKeys = clearAllCaches();
                          queryKeys.forEach(key => queryClient.invalidateQueries({ queryKey: key }));
                          setAllCacheCleared(true);
                          setTimeout(() => setAllCacheCleared(false), 1500);
                        }}
                        className={cn(
                          "p-1 rounded transition-colors",
                          allCacheCleared
                            ? "text-green-600"
                            : "text-yellow-600 hover:text-yellow-800 hover:bg-yellow-200"
                        )}
                      >
                        <RefreshCw className={cn("h-3 w-3", allCacheCleared && "animate-spin")} />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="text-[10px]">
                      {allCacheCleared ? "All caches cleared!" : "Clear all caches"}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <button
                  onClick={clearRequests}
                  className="text-yellow-600 hover:text-yellow-800 p-1 rounded hover:bg-yellow-200 transition-colors"
                  title="Clear requests"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            </div>
            
            {requests.length === 0 ? (
              <div className="p-4 text-center text-yellow-600 italic">
                No API requests captured yet
              </div>
            ) : (
              <div className="divide-y divide-yellow-200">
                {requests.map(request => (
                  <RequestItem key={request.id} request={request} />
                ))}
              </div>
            )}
          </div>
        ) : activeTab === 'simulate' ? (
          <div className="p-3 space-y-4 text-yellow-950">
            <div className="text-[10px] text-yellow-700 leading-tight">
              Select a simulated response for each endpoint. When set, form submissions will return the selected response instead of calling the real API.
            </div>
            {simulatedEndpoints.map(endpoint => {
              const current = simulatedResponses[endpoint.id];
              return (
                <div key={endpoint.id} className="space-y-1.5">
                  <div className="font-bold text-yellow-700 text-[11px]">{endpoint.label}</div>
                  {endpoint.description && (
                    <div className="text-[10px] text-yellow-600">{endpoint.description}</div>
                  )}
                  <div className="space-y-1">
                    <label className="flex items-center gap-2 px-2 py-1 rounded hover:bg-yellow-200/50 cursor-pointer">
                      <input
                        type="radio"
                        name={`sim-${endpoint.id}`}
                        checked={!current}
                        onChange={() => setSimulatedResponse(endpoint.id, null)}
                        className="accent-yellow-600"
                      />
                      <span className="text-[11px]">None (real API call)</span>
                    </label>
                    {endpoint.responses.map(resp => (
                      <label
                        key={resp.status}
                        className={cn(
                          "flex items-center gap-2 px-2 py-1 rounded cursor-pointer",
                          current?.status === resp.status
                            ? "bg-orange-200 text-orange-900"
                            : "hover:bg-yellow-200/50"
                        )}
                      >
                        <input
                          type="radio"
                          name={`sim-${endpoint.id}`}
                          checked={current?.status === resp.status}
                          onChange={() => setSimulatedResponse(endpoint.id, { status: resp.status })}
                          className="accent-orange-600"
                        />
                        <span className="text-[11px]">
                          <span className={cn(
                            "font-bold mr-1",
                            resp.status >= 400 ? "text-red-600" : "text-green-600"
                          )}>
                            {resp.status}
                          </span>
                          {resp.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : activeTab === 'columns' ? (
          <div className="p-3 space-y-3 text-yellow-950">
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-yellow-700">
                Saved column preferences for <span className="font-semibold">{username}</span>
              </span>
              <button
                onClick={() => {
                  clearColumnPreferences(username);
                  setColumnPrefsVersion((v) => v + 1);
                }}
                className="text-[10px] text-red-600 hover:text-red-800 font-medium"
              >
                Clear All
              </button>
            </div>
            {(() => {
              const prefs = getAllColumnPreferences(username);
              if (prefs.length === 0) {
                return (
                  <div className="text-center text-yellow-600 italic py-4 text-[11px]">
                    No saved column preferences
                  </div>
                );
              }
              return prefs.map((pref) => (
                <div key={pref.tableId} className="bg-yellow-200/50 px-2 py-1.5 rounded">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-[11px]">{pref.tableId}</span>
                    <button
                      onClick={() => {
                        clearColumnPreferences(username, pref.tableId);
                        setColumnPrefsVersion((v) => v + 1);
                      }}
                      className="text-[10px] text-red-600 hover:text-red-800"
                    >
                      Reset
                    </button>
                  </div>
                  {pref.hidden.length > 0 ? (
                    <div className="text-[10px] text-yellow-700 mt-0.5">
                      Hidden: {pref.hidden.join(', ')}
                    </div>
                  ) : (
                    <div className="text-[10px] text-yellow-600 italic mt-0.5">All columns visible</div>
                  )}
                  {pref.pageSize && (
                    <div className="text-[10px] text-yellow-700 mt-0.5">
                      Page size: {pref.pageSize}
                    </div>
                  )}
                </div>
              ));
            })()}
          </div>
        ) : activeTab === 'caches' ? (
          <div className="p-3 space-y-2 text-yellow-950">
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-yellow-700">
                Registered caches and last-cleared times
              </span>
              <button
                onClick={() => {
                  const queryKeys = clearAllCaches();
                  queryKeys.forEach(key => queryClient.invalidateQueries({ queryKey: key }));
                  setAllCacheCleared(true);
                  setCachesVersion(v => v + 1);
                  setTimeout(() => setAllCacheCleared(false), 1500);
                }}
                className={cn(
                  "text-[10px] font-medium px-2 py-0.5 rounded transition-colors",
                  allCacheCleared
                    ? "bg-green-200 text-green-800"
                    : "bg-yellow-300 text-yellow-900 hover:bg-yellow-400"
                )}
              >
                {allCacheCleared ? "Cleared ✓" : "Clear all"}
              </button>
            </div>
            {(() => {
              void cachesVersion;
              const caches = listCaches();
              if (caches.length === 0) {
                return (
                  <div className="text-center text-yellow-600 italic py-4 text-[11px]">
                    No registered caches
                  </div>
                );
              }
              return caches.map(c => (
                <div key={c.label} className="bg-yellow-200/50 px-2 py-1.5 rounded">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-[11px] truncate" title={c.label}>{c.label}</span>
                    <button
                      onClick={() => {
                        c.clear();
                        if (c.queryKeys.length > 0) {
                          queryClient.invalidateQueries({ queryKey: c.queryKeys });
                        }
                        setCachesVersion(v => v + 1);
                      }}
                      className="text-[10px] text-red-600 hover:text-red-800 font-medium flex-shrink-0"
                    >
                      Clear
                    </button>
                  </div>
                  <div className="text-[10px] text-yellow-700 mt-0.5 truncate" title={c.endpoints.join(', ')}>
                    {c.endpoints.length} endpoint{c.endpoints.length !== 1 ? 's' : ''}: {c.endpoints.join(', ')}
                  </div>
                  <div className="text-[10px] text-yellow-700 mt-0.5">
                    Last cleared: {c.lastClearedAt ? new Date(c.lastClearedAt).toLocaleTimeString() : <span className="italic text-yellow-600">never this session</span>}
                  </div>
                </div>
              ));
            })()}
          </div>
        ) : null}
      </div>
    </div>
  );
}
